import React, { useState, useEffect } from 'react';
import { stationAmenityAPI, stationAPI } from '../services/api';
import { StationAmenity, Station } from '../types';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TextField,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  FormControlLabel,
  Switch
} from '@mui/material';
import { Edit, Delete, Refresh, Add } from '@mui/icons-material';

// Define proper error type
interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
}

const StationAmenities: React.FC = () => {
  const [amenities, setAmenities] = useState<StationAmenity[]>([]);
  const [stations, setStations] = useState<Station[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({
    stationId: '',
    amenityType: 'PARKING' as 'PARKING' | 'WHEELCHAIR_ACCESS' | 'ESCALATOR' | 'ELEVATOR' | 'RESTROOM' | 'FOOD_COURT' | 'ATM' | 'TICKET_COUNTER' | 'INFORMATION_DESK',
    isAvailable: true,
    description: '',
    location: '',
    status: 'ACTIVE',
    operatingHours: '',
    cost: '',
    currency: 'USD',
    contactNumber: ''
  });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [amenitiesRes, stationsRes] = await Promise.all([
        stationAmenityAPI.getAll(),
        stationAPI.getAll()
      ]);
      setAmenities(amenitiesRes.data);
      setStations(stationsRes.data);
    } catch (e: unknown) {
      const error = e as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setForm({ 
      ...form, 
      [name]: type === 'number' ? parseFloat(value) || 0 : value 
    });
    setFormError('');
  };

  const handleSelectInput = (e: SelectChangeEvent<string>) => {
    const name = e.target.name as string;
    setForm({ ...form, [name]: e.target.value });
    setFormError('');
  };

  const openAddModal = () => {
    setIsEditMode(false);
    setEditingId(null);
    setForm({
      stationId: '',
      amenityType: 'PARKING',
      isAvailable: true,
      description: '',
      location: '',
      status: 'ACTIVE',
      operatingHours: '',
      cost: '',
      currency: 'USD',
      contactNumber: ''
    });
    setFormError('');
    setShowModal(true);
  };

  const openEditModal = (amenity: StationAmenity) => {
    setIsEditMode(true);
    setEditingId(amenity.id!);
    setForm({
      stationId: amenity.stationId,
      amenityType: amenity.amenityType,
      isAvailable: amenity.isAvailable || true,
      description: amenity.description || '',
      location: amenity.location || '',
      status: amenity.status || 'ACTIVE',
      operatingHours: amenity.operatingHours || '',
      cost: amenity.cost ? amenity.cost.toString() : '',
      currency: amenity.currency || 'USD',
      contactNumber: amenity.contactNumber || ''
    });
    setFormError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setFormError('');
  };

  const handleSave = async () => {
    if (!form.stationId || !form.amenityType) {
      setFormError('Station and Amenity Type are required');
      return;
    }

    setSaving(true);
    try {
      const amenityData = {
        ...form,
        cost: form.cost ? parseFloat(form.cost) : undefined
      };

      if (isEditMode && editingId) {
        await stationAmenityAPI.update(editingId, amenityData);
        setSuccessMsg('Amenity updated successfully');
      } else {
        await stationAmenityAPI.create(amenityData);
        setSuccessMsg('Amenity created successfully');
      }
      await fetchData();
      closeModal();
    } catch (e: unknown) {
      const error = e as ApiError;
      setFormError(error.response?.data?.message || error.message || 'Failed to save amenity');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this amenity?')) return;
    
    setDeletingId(id);
    try {
      await stationAmenityAPI.delete(id);
      setSuccessMsg('Amenity deleted successfully');
      await fetchData();
    } catch (e: unknown) {
      const error = e as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to delete amenity');
    } finally {
      setDeletingId(null);
    }
  };

  const getStationName = (stationId: string) => {
    const station = stations.find(s => s.id === stationId);
    return station?.name || 'Unknown Station';
  };

  const getAmenityTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      'PARKING': 'Parking',
      'WHEELCHAIR_ACCESS': 'Wheelchair Access',
      'ESCALATOR': 'Escalator',
      'ELEVATOR': 'Elevator',
      'RESTROOM': 'Restroom',
      'FOOD_COURT': 'Food Court',
      'ATM': 'ATM',
      'TICKET_COUNTER': 'Ticket Counter',
      'INFORMATION_DESK': 'Information Desk'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, px: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Station Amenities
        </Typography>
        <Button variant="contained" startIcon={<Add />} onClick={openAddModal}>
          Add Amenity
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mx: 3, mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mx: 3, mb: 3 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Station</TableCell>
                <TableCell>Amenity Type</TableCell>
                <TableCell>Available</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Cost</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {amenities.map((amenity) => (
                <TableRow key={amenity.id}>
                  <TableCell>{getStationName(amenity.stationId)}</TableCell>
                  <TableCell>{getAmenityTypeLabel(amenity.amenityType)}</TableCell>
                  <TableCell>
                    <Chip 
                      label={amenity.isAvailable ? 'Yes' : 'No'} 
                      color={amenity.isAvailable ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{amenity.location || 'N/A'}</TableCell>
                  <TableCell>
                    <Chip 
                      label={amenity.status || 'ACTIVE'} 
                      color={amenity.status === 'ACTIVE' ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {amenity.cost ? `${amenity.cost} ${amenity.currency || 'USD'}` : 'Free'}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Edit">
                      <IconButton onClick={() => openEditModal(amenity)} size="small">
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton 
                        onClick={() => handleDelete(amenity.id!)} 
                        size="small"
                        disabled={deletingId === amenity.id}
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onClose={closeModal} maxWidth="md" fullWidth>
        <DialogTitle>
          {isEditMode ? 'Edit Station Amenity' : 'Add Station Amenity'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Station</InputLabel>
                <Select
                  name="stationId"
                  value={form.stationId}
                  onChange={handleSelectInput}
                  label="Station"
                >
                  {stations.map((station) => (
                    <MenuItem key={station.id} value={station.id}>
                      {station.name} ({station.stationCode})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Amenity Type</InputLabel>
                <Select
                  name="amenityType"
                  value={form.amenityType}
                  onChange={handleSelectInput}
                  label="Amenity Type"
                >
                  <MenuItem value="PARKING">Parking</MenuItem>
                  <MenuItem value="WHEELCHAIR_ACCESS">Wheelchair Access</MenuItem>
                  <MenuItem value="ESCALATOR">Escalator</MenuItem>
                  <MenuItem value="ELEVATOR">Elevator</MenuItem>
                  <MenuItem value="RESTROOM">Restroom</MenuItem>
                  <MenuItem value="FOOD_COURT">Food Court</MenuItem>
                  <MenuItem value="ATM">ATM</MenuItem>
                  <MenuItem value="TICKET_COUNTER">Ticket Counter</MenuItem>
                  <MenuItem value="INFORMATION_DESK">Information Desk</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isAvailable}
                    onChange={(e) => setForm({ ...form, isAvailable: e.target.checked })}
                  />
                }
                label="Available"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={form.status}
                  onChange={handleSelectInput}
                  label="Status"
                >
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="MAINTENANCE">Maintenance</MenuItem>
                  <MenuItem value="CLOSED">Closed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Location"
                name="location"
                value={form.location}
                onChange={handleInput}
                placeholder="e.g., Platform 1, Near Exit A"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Operating Hours"
                name="operatingHours"
                value={form.operatingHours}
                onChange={handleInput}
                placeholder="e.g., 6:00 AM - 11:00 PM"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Cost"
                name="cost"
                type="number"
                value={form.cost}
                onChange={handleInput}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Currency</InputLabel>
                <Select
                  name="currency"
                  value={form.currency}
                  onChange={handleSelectInput}
                  label="Currency"
                >
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="EUR">EUR</MenuItem>
                  <MenuItem value="GBP">GBP</MenuItem>
                  <MenuItem value="INR">INR</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={form.description}
                onChange={handleInput}
                multiline
                rows={3}
                placeholder="Additional details about the amenity"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Contact Number"
                name="contactNumber"
                value={form.contactNumber}
                onChange={handleInput}
                placeholder="Contact number for inquiries"
              />
            </Grid>
          </Grid>
          {formError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {formError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal}>Cancel</Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Message */}
      <Snackbar
        open={!!successMsg}
        autoHideDuration={6000}
        onClose={() => setSuccessMsg('')}
      >
        <Alert onClose={() => setSuccessMsg('')} severity="success">
          {successMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StationAmenities; 